import React from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock, MapPin, Repeat, Video } from "lucide-react";
import { DeskCalendarEvent } from "types/Desk";
import { describeMeeting, meetingWeekday } from "utils/deskMeeting";

/**
 * The "when is this meeting?" banner.
 *
 * A Teams/Outlook invite puts the time ONLY in its .ics part — the mail body
 * a card shows is a join link and nothing else. The backend parses that part
 * into `calendarEvent`; this draws it above the body, where the question is
 * actually asked.
 */
interface Props {
  event: DeskCalendarEvent;
}

export default function DeskMeetingBanner({ event }: Props) {
  const { t, i18n } = useTranslation();
  const locale = (i18n as any).language === "en" ? "en-GB" : "lt-LT";
  const shown = describeMeeting(event, locale);
  const weekday = meetingWeekday(event, locale);

  // The invite is attacker-supplied mail. The parser only ever stores http(s)
  // links, but a Join button is a first-party click target — re-check here so
  // the component cannot be made to render `javascript:` by a future producer.
  const joinUrl =
    event.conferenceUrl && /^https?:\/\//i.test(event.conferenceUrl) ? event.conferenceUrl : null;

  const tone = shown.isCancelled
    ? "border-red-300/60 bg-red-500/5 dark:border-red-900"
    : "border-indigo-300/60 bg-indigo-500/5 dark:border-indigo-900";
  const iconTone = shown.isCancelled ? "text-red-500" : "text-indigo-500";

  return (
    <div className={`mt-2 rounded-xl border p-3 ${tone}`}>
      <div className="flex items-start gap-2.5">
        <CalendarClock className={`mt-0.5 h-5 w-5 shrink-0 ${iconTone}`} aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
              {shown.date}
              {shown.time ? ` · ${shown.time}` : ""}
            </span>
            {weekday && <span className="text-xs text-zinc-500">{weekday}</span>}
            {shown.isCancelled && (
              <span className="rounded bg-red-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-600 dark:text-red-400">
                {t("desk_meeting_cancelled")}
              </span>
            )}
            {event.allDay && <span className="text-xs text-zinc-500">{t("desk_meeting_all_day")}</span>}
          </div>

          {/* Times we could not anchor to a zone are shown as the sender wrote
              them, with their own zone label — never silently shifted. */}
          <div className="text-[11px] text-zinc-500">
            {shown.isFloating
              ? `${t("desk_meeting_sender_time")}${shown.zone ? ` · ${shown.zone}` : ""}`
              : shown.zone}
            {event.isRecurring && (
              <span className="ml-2 inline-flex items-center gap-1">
                <Repeat className="h-3 w-3" aria-hidden="true" />
                {t("desk_meeting_recurring")}
              </span>
            )}
          </div>

          {event.summary && (
            <div className="truncate text-xs font-semibold text-zinc-700 dark:text-zinc-300">{event.summary}</div>
          )}

          {event.location && (
            <div className="flex items-center gap-1 truncate text-[11px] text-zinc-500">
              <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{event.location}</span>
            </div>
          )}

          {event.organizer && (event.organizer.name || event.organizer.email) && (
            <div className="truncate text-[11px] text-zinc-500">
              {t("desk_meeting_organizer")}: {event.organizer.name || event.organizer.email}
            </div>
          )}

          {joinUrl && !shown.isCancelled && (
            <a
              href={joinUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
            >
              <Video className="h-3.5 w-3.5" aria-hidden="true" />
              {t("desk_meeting_join")}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
